import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";

const links = [
  { to: "/", label: "Start", testid: "nav-link-start" },
  { to: "/leistungen", label: "Leistungen", testid: "nav-link-leistungen" },
  { to: "/ueber-uns", label: "Über uns", testid: "nav-link-ueber-uns" },
  { to: "/kontakt", label: "Kontakt", testid: "nav-link-kontakt" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
        scrolled ? "bg-[#090A0C]/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/assets/logo-mark.png"
            alt="HeroPhone Logo"
            className="h-11 w-auto transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105"
          />
          <span className="font-display text-2xl font-extrabold italic uppercase tracking-wide leading-none">
            <span className="text-[#FFE600]">Hero</span>
            <span className="text-white">Phone</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Hauptnavigation">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              className={({ isActive }) =>
                `font-mono2 text-[13px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[#FFE600]" : "text-gray-400 hover:text-white"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+4915116012024"
            data-testid="nav-call-button"
            className="flex items-center gap-2 font-mono2 text-[13px] tracking-wider text-gray-300 hover:text-[#FFE600] transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            0151 16012024
          </a>
          <Link
            to="/kontakt"
            data-testid="nav-cta-button"
            className="ml-2 bg-[#FFE600] text-[#090A0C] font-display font-semibold uppercase tracking-wide px-5 py-2.5 rounded-sm text-base hover:bg-[#FFF033] hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
          >
            Reparatur anfragen
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="Menü öffnen"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#090A0C]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-${link.testid}`}
                    className={({ isActive }) =>
                      `font-display text-3xl font-semibold uppercase ${
                        isActive ? "text-[#FFE600]" : "text-white"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href="tel:+4915116012024"
                data-testid="mobile-call-button"
                className="flex items-center gap-2 font-mono2 text-sm tracking-wider text-[#FFE600] pt-2"
              >
                <Phone className="w-4 h-4" />
                0151 16012024
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
